import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { jsPDF } from 'jspdf';
import autoTable from "jspdf-autotable";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions,     
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from "state/axios";
import { getFlagUrl, formatTZDate } from 'state/helpers';
import CrewListDialog from 'components/CrewListDialog';
import FlexBetween from 'components/FlexBetween'; 
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const LegMembersDialog = ({ 
    open, 
    onClose, 
    crewCode, 
    crewName, 
    crewLegs,
}) => {
    useAuthCheck();
    const dispatch = useDispatch();
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const neutralDark = theme.palette.neutral.dark;    
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [dialogState, setDialogState] = useState({
        openCrewList: false,
        flight: [],
        crewList: [],
        jumpList: [],
    });

    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const AIRLINE_NAME = paramsMap.get("AIRLINE_NAME") || "";
    const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";   
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
    
    const getTime = (flight, timeMode, ...fields) => {
        for (const field of fields) {
            const val = timeMode === 'UTC' ? flight[field] : flight[`${field}LocalBase`];
            if (val) return val;
        }
        return '';
    };
    
    const handleCloseDialog = () => {  
        onClose();  
    };

    const calcTotalBlockTimeAndSectors = (obj) => {
        let totalBlockMinutes = 0;
        let totalAdjustedBlockHours = 0;
        let totalSectors = 0;

        Object.values(obj).forEach(rosters => {
            rosters?.forEach(roster => {
                if (roster.blockMinutes > 0 && !roster.pax) {
                    totalBlockMinutes += roster.blockMinutes || 0;
                    totalAdjustedBlockHours += roster.adjustedBlockHours || 0;
                    totalSectors += 1; 
                }
            });
        });

        return {
            totalBlockHours: (totalBlockMinutes / 60).toFixed(2),
            totalAdjustedBlockHours,
            totalSectors
        };
    };

    const { totalBlockHours, totalSectors } = calcTotalBlockTimeAndSectors(crewLegs);

    const sortedCrewLegs = useMemo(() => {
        if (!crewLegs) return {};
        return Object.fromEntries(
            Object.entries(crewLegs)
                .sort(([a], [b]) => new Date(a) - new Date(b))
                .map(([date, flights]) => [
                    date,
                    [...flights].sort((a, b) => new Date(a.fullAtd || a.fullEtd || a.fullStd || 0) - new Date(b.fullAtd || b.fullEtd || b.fullStd || 0)),
                ])
        );
    }, [crewLegs])

    const handleViewClick = useCallback(async (flightDate, flightNo, dep) => {
        try {
            const url = `/legmembers/legmembersbyflightno`;
            const payload = { flightDate, flightNo, dep };
            
            const res = await api.post(url, payload);            
            const { flightDetails, legMembers, jumpSeaters } = res.data;
            
            setDialogState({
                openCrewList: true,
                flight: flightDetails,
                crewList: legMembers,
                jumpList: jumpSeaters,
            });
        } catch (error) {
            setDialogState(prev => ({ ...prev, openCrewList: false }));
        } 
    }, []);
    
    const handleCloseCrewListDialog = () => {
        setDialogState(prev => ({ ...prev, openCrewList: false }));
    }; 

    const exportToExcel = (crewCode, crewName, crewLegs) => {
        if (!crewLegs) return;

        let rows = [];
        let totalBlockHours = 0;
        let totalAdjustedBlockHours = 0;

        Object.entries(sortedCrewLegs).forEach(([dateKey, rosters]) => {
            rosters?.forEach(item => {
                const blockHours = !item.pax ? (item.blockHours || 0) : 0;
                const adjustedBlockHours = !item.pax ? (item.adjustedBlockHours || 0) : 0;

                if (blockHours) {
                    totalBlockHours += blockHours;
                    totalAdjustedBlockHours += adjustedBlockHours;
                }
                
                rows.push({
                    Date: formatTZDate(item.fullEtd || item.flightDate, 'UTC', 'dd.MMM.yy'),
                    FlightNo: item.flightNo || "",
                    // Route: item.routeNo || "",
                    Sector: item.dep && item.arr ? `${item.dep}-${item.arr}` : '',
                    DepTime: item.etd || "",
                    ArrTime: item.eta || "",
                    Pax: item.pax ? "Yes" : "No",
                    // Training: item.trnDuty || "",
                    BlockHours: blockHours ? blockHours.toFixed(2) : 0,
                    AdjustedBlockHours: adjustedBlockHours ? adjustedBlockHours?.toFixed(2) : 0,
                });
            });
        });

        // Add row at last
        rows.push({
            Date: "",
            FlightNo: "",
            Sector: "",
            DepTime: "",
            ArrTime: "",
            Pax: "Total:",
            // Training: "",
            BlockHours: totalBlockHours ? totalBlockHours?.toFixed(2): 0,
            AdjustedBlockHours: totalAdjustedBlockHours ? totalAdjustedBlockHours.toFixed(2) : 0,
        });
        
        // Create worksheet
        const worksheet = XLSX.utils.json_to_sheet(rows);

        // Create workbook
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "PersonalCrewSchedule");

        const fileName = `${crewCode}_${crewName}.xlsx`;

        // Write file
        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const data = new Blob([excelBuffer], { type: "application/octet-stream" });
        saveAs(data, fileName);
    };

    const exportToPDF = () => {
        if (!crewLegs) return;

        const doc = new jsPDF("l", "mm", "a4"); // landscape cho dễ nhìn
        const pageWidth = doc.internal.pageSize.getWidth();

        // ===== TITLE =====
        doc.setFontSize(14);
        if (AIRLINE_NAME) {
            doc.text(AIRLINE_NAME, 15, 12);
        }
        doc.text(`${crewCode} - ${crewName}`, pageWidth / 2, 12, { align: "center" });

        doc.setFontSize(10);
        // doc.text(
        //     `Time mode: ${timeMode}`,
        //     pageWidth - 15,
        //     12,
        //     { align: "right" }
        // );

        // ===== DATA PREPARE =====
        let bodyRows = [];
        let totalBlockHours = 0;
        let totalAdjustedBlockHours = 0;

        Object.entries(sortedCrewLegs).forEach(([_, rosters]) => {
            rosters?.forEach(item => {
                const blockHours = !item.pax ? (item.blockHours || 0) : 0;
                const adjustedBlockHours = !item.pax ? (item.adjustedBlockHours || 0) : 0;

                if (blockHours) {
                    totalBlockHours += blockHours;
                    totalAdjustedBlockHours += adjustedBlockHours;
                }

                bodyRows.push({
                    date: formatTZDate(
                        item.fullEtd || item.flightDate,
                        "UTC",
                        "dd.MMM.yy"
                    ),
                    flightNo: item.flightNo || "",
                    sector: item.dep && item.arr ? `${item.dep}-${item.arr}` : "",
                    etd: item.etd || "",
                    eta: item.eta || "",
                    pax: item.pax ? "Yes" : "No",
                    blockHours: blockHours && blockHours.toFixed(2),
                    adjustedBlockHours: adjustedBlockHours && adjustedBlockHours?.toFixed(2),
                    isAugmented: item.isAugmented === true, // flag
                });
            });
        });

        const columns = [
            { header: "Date", dataKey: "date" },
            { header: "Flight No", dataKey: "flightNo" },
            { header: "Sector", dataKey: "sector" },
            { header: "Departure", dataKey: "etd" },
            { header: "Arrival", dataKey: "eta" },
            { header: "Pax", dataKey: "pax" },
            { header: "BH", dataKey: "blockHours" },
            { header: "Cumulative BH", dataKey: "adjustedBlockHours" },
        ];

        // ===== TABLE =====
        autoTable(doc, {
            startY: 18,
            columns,
            body: bodyRows,
            styles: {
                fontSize: 9,
                cellPadding: 2,
            },
            headStyles: {
                fillColor: [33, 150, 243],
                textColor: 255,
                halign: "center",
            },
            columnStyles: {
                date: { halign: "center" },
                flightNo: { halign: "center" },
                sector: { halign: "center" },
                etd: { halign: "center" },
                eta: { halign: "center" },
                pax: { halign: "center" },
                blockHours: { halign: "center" },
                adjustedBlockHours: { halign: "center" },
            },
            didParseCell: function (data) {
                // Chỉ tô body
                if (data.section === "body") {
                    const row = data.row.raw;

                    // Cột AdjustedBH
                    if (
                        data.column.dataKey === "adjustedBlockHours" &&
                        row.isAugmented &&
                        row.adjustedBlockHours
                    ) {
                        data.cell.styles.fillColor = [255, 235, 59]; // yellow
                        data.cell.styles.textColor = 0;
                        data.cell.styles.fontStyle = "bold";
                    }
                }
            },
            didDrawPage: () => {
                doc.setFontSize(9);
                doc.text(
                    `Total BH: ${totalBlockHours.toFixed(2)} | Total Cumulative BH: ${totalAdjustedBlockHours.toFixed(2)} | Sectors: ${totalSectors}`,
                    pageWidth - 15,
                    doc.internal.pageSize.getHeight() - 8,
                    { align: "right" }
                );
            }
        });

        // ===== SAVE =====
        doc.save(`${crewCode}-${crewName}.pdf`);
    };

    const hasData = crewLegs && Object.keys(crewLegs).length > 0;


    return (
        <Dialog 
            open={open} 
            onClose={handleCloseDialog}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '96%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${neutralDark}`}>
                <FlexBetween>
                    <Typography fontWeight="bold" color="primary">
                        {crewCode} - {crewName}
                    </Typography>
                    <Box onClick={() => dispatch(setTimeMode())}>
                        <img
                            alt="Flag"
                            width={25}
                            height={25}
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>
            </DialogTitle>

            <DialogContent>
                {!hasData && (
                    <Typography>No flights available</Typography>
                )} 
                {Object.entries(sortedCrewLegs)?.map(([key, flights]) => (
                    <Box key={key} py={1} borderBottom={`1px solid ${neutralDark}`}>
                        {flights?.map((f, idx) => {                            
                            return (                        
                                <FlexBetween key={idx} px={0.5} gap={1}>
                                    <Typography 
                                        width="18px" 
                                        textAlign="left" 
                                        color="primary"
                                        sx={{
                                            // textDecoration: 'underline',
                                            '&:hover': {
                                                cursor: 'pointer',
                                            },
                                        }}
                                        onClick={() => {
                                            const flightNo = isNaN(Number(f.flightNo)) ? null : Number(f.flightNo);
                                            if (flightNo) {
                                                handleViewClick(f.flightDate, flightNo, f.dep)
                                            }
                                        }}
                                    >
                                        {f.flightNo}
                                    </Typography>
                                    <Typography width="40px" textAlign="center">
                                        {timeMode === 'UTC' 
                                            ? formatTZDate(f.fullAtd || f.fullEtd || f.flightDate, 'UTC', 'ddMMMyy') 
                                            : formatTZDate(f.fullAtdLocalBase || f.fullEtdLocalBase || f.flightDateLocalBase, 'UTC', 'ddMMMyy')
                                        }
                                    </Typography>
                                    <Typography width="20px" textAlign="center">
                                        {getTime(f, timeMode, 'atd', 'etd')}
                                    </Typography>
                                    <Typography width="16px" textAlign="center">{f.dep}</Typography>
                                    <Typography width="16px" textAlign="center">{f.arr}</Typography>
                                    <Typography width="20px" textAlign="center">
                                        {getTime(f, timeMode, 'ata', 'eta')}
                                    </Typography> 
                                    <Typography width="4px" textAlign="center">{f.pax}</Typography>
                                    <Typography width="4px" textAlign="center">{f.trnDuty}</Typography>                                    
                                </FlexBetween>
                            )}
                        )}
                    </Box>
                ))}
            </DialogContent>
            <DialogActions
                sx={{
                    justifyContent: hasData ? "space-between" : "flex-end",
                    alignItems: "center",
                }}
            >
                {hasData && 
                    <Box 
                        sx={{
                            display: 'flex', 
                            alignItems: 'center',
                            borderBottom: '1px solid', 
                            borderColor: 'primary.main',
                            p: 0.5, 
                            gap: 1,
                        }}
                    >
                        <Typography>BH: {totalBlockHours}</Typography>
                        {/* <Typography>CBH: {totalAdjustedBlockHours.toFixed(2)}</Typography> */}
                        <Typography>Sectors: {totalSectors}</Typography>
                    </Box>
                }
                                
                <Box display="flex" gap={1}>
                    {/* <Button 
                        variant="outlined" 
                        disabled={!hasData}
                        onClick={() => exportToExcel(crewCode, crewName, crewLegs)}
                    >
                        Excel
                    </Button> */}
                    {/* <Button
                        variant="outlined"
                        disabled={!hasData}
                        onClick={exportToPDF}
                    >
                        PDF
                    </Button> */}
                    <Button 
                        variant="outlined" 
                        color="secondary" 
                        onClick={handleCloseDialog}
                    >
                        Close
                    </Button>
                </Box>
            </DialogActions>    

            {dialogState.openCrewList &&
                <CrewListDialog 
                    open={dialogState.openCrewList}
                    onClose={handleCloseCrewListDialog}
                    flight={dialogState.flight}
                    crewList={dialogState.crewList}
                    jumpList={dialogState.jumpList}
                />
            }     
        </Dialog>
    );
};

export default LegMembersDialog;
